import { useState } from 'react';
import { Button } from '../ui/Button';
import { Dialog } from '@headlessui/react';
import { MonthlyDataForm } from './MonthlyDataForm';
import { MonthlyDataTable } from './MonthlyDataTable';
import { YearlyStats } from './YearlyStats';
import { MonthlyStats } from '../../types/property';
import { createMonthlyStats, updateMonthlyStats, deleteMonthlyStats } from '../../services/stats';
import { useAuthStore } from '../../store/authStore';
import { Select } from '../ui/Select';
import toast from 'react-hot-toast';

interface MonthlyDataManagerProps {
  propertyId: string;
  monthlyStats: MonthlyStats[];
  onDataChange: () => Promise<void>;
}

export const MonthlyDataManager = ({ 
  propertyId, 
  monthlyStats, 
  onDataChange 
}: MonthlyDataManagerProps) => {
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState<MonthlyStats | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const user = useAuthStore(state => state.user);
  const isAdmin = user?.role === 'admin';

  // Obtener años únicos de los datos
  const years = [...new Set(monthlyStats.map(stat => stat.year))].sort((a, b) => b - a);

  // Filtrar datos por año seleccionado
  const filteredStats = selectedYear === 'all' 
    ? monthlyStats 
    : monthlyStats.filter(stat => stat.year === parseInt(selectedYear));

  const handleSubmit = async (data: Omit<MonthlyStats, 'id' | 'created_at'>) => {
    if (!isAdmin) {
      toast.error('Solo los administradores pueden modificar los datos');
      return;
    }

    try {
      if (editingData) {
        await updateMonthlyStats(editingData.id, data);
        toast.success('Datos actualizados correctamente');
      } else {
        await createMonthlyStats(data);
        toast.success('Datos añadidos correctamente');
      }
      setShowModal(false);
      setEditingData(null);
      await onDataChange();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar los datos');
    }
  };

  const handleDelete = async (id: string) => {
    if (!isAdmin) {
      toast.error('Solo los administradores pueden eliminar datos');
      return;
    }

    if (!window.confirm('¿Estás seguro de que deseas eliminar estos datos?')) return;

    try {
      await deleteMonthlyStats(id, propertyId);
      toast.success('Datos eliminados correctamente');
      await onDataChange();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar los datos');
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Resumen Anual</h2>
          <p className="text-gray-600">
            Vista general del rendimiento anual de la propiedad, incluyendo facturación, 
            beneficios, ocupación y otros indicadores clave.
          </p>
        </div>
        <YearlyStats monthlyStats={monthlyStats} />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold mb-2">Datos Mensuales</h2>
            <p className="text-gray-600">
              Registro detallado mes a mes de todos los indicadores de rendimiento 
              de la propiedad, incluyendo ingresos, gastos y estadísticas de ocupación.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              options={[
                { value: 'all', label: 'Todos los años' },
                ...years.map(year => ({
                  value: year.toString(),
                  label: year.toString()
                }))
              ]}
              className="w-40"
            />
            {isAdmin && (
              <Button
                onClick={() => {
                  setEditingData(null);
                  setShowModal(true);
                }}
              >
                Añadir Datos Mensuales
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          {filteredStats.length > 0 ? (
            <MonthlyDataTable
              data={filteredStats}
              onEdit={isAdmin ? (data) => {
                setEditingData(data);
                setShowModal(true);
              } : undefined}
              onDelete={isAdmin ? handleDelete : undefined}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              No hay datos mensuales registrados
              {selectedYear !== 'all' && ` para el año ${selectedYear}`}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen px-4">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

          <div className="relative bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <Dialog.Title className="text-lg font-semibold mb-4">
              {editingData ? 'Editar Datos Mensuales' : 'Añadir Datos Mensuales'}
            </Dialog.Title>

            <MonthlyDataForm
              propertyId={propertyId}
              initialData={editingData}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
